import React, { useEffect, useMemo, useState } from 'react';
import {
  X, UserPlus, Loader2, CheckCircle2, Copy, AlertTriangle,
  ChevronLeft, ChevronRight, Upload, FileText, Trash2, Check,
  Mail, Send, FileDown,
} from 'lucide-react';

/**
 * Employee Onboarding Wizard — fired from HR Command Center.
 * 5-step ERP-grade flow:
 *   1. Identity & Contact     (type, names, email, phone, DOB, gender)
 *   2. Address & Personal     (full address, blood group)
 *   3. Employment & Skills    (role, dept, manager, NDA, joining, last co.)
 *   4. CTC Breakdown          (annual → auto-prefill, every row editable)
 *   5. Documents & Review     (KYC uploads, send-welcome, summary)
 * Then success screen with one-click Appointment Letter generate + dispatch.
 */
const STEPS = [
  { id: 1, label: 'Identity' },
  { id: 2, label: 'Address' },
  { id: 3, label: 'Employment' },
  { id: 4, label: 'Compensation' },
  { id: 5, label: 'Documents & Review' },
];

const DOC_KINDS = [
  { v: 'resume', l: 'Resume / CV' },
  { v: 'aadhaar', l: 'Aadhaar' },
  { v: 'pan', l: 'PAN' },
  { v: 'passport', l: 'Passport' },
  { v: 'education', l: 'Education Certificate' },
  { v: 'experience_letter', l: 'Experience Letter' },
  { v: 'relieving_letter', l: 'Relieving Letter' },
  { v: 'salary_slip', l: 'Last Salary Slip' },
  { v: 'bank_statement', l: 'Bank / Cancelled Cheque' },
  { v: 'offer_letter', l: 'Offer Letter (incoming)' },
  { v: 'other', l: 'Other' },
];

const blankAddress = { street: '', city: '', state: '', pincode: '', country: 'India', landmark: '' };
const blankCTC = {
  annual: 0, basic: 0, hra: 0, special_allowance: 0,
  pf_employee: 0, pf_employer: 0, prof_tax: 0,
  tds_annual: 0, tds_monthly: 0,
  gross_annual: 0, gross_monthly: 0,
  net_annual: 0, net_monthly: 0,
};

export default function EmployeeOnboardModal({ api, onClose, onCreated }) {
  const [vocab, setVocab] = useState(null);
  const [managers, setManagers] = useState([]);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [docKind, setDocKind] = useState('resume');
  const [uploading, setUploading] = useState(false);
  const [letterBusy, setLetterBusy] = useState(false);
  const [letterStatus, setLetterStatus] = useState(null); // 'generated' | 'dispatched' | null

  const [form, setForm] = useState({
    // identity
    workforce_type: 'employee',
    first_name: '', last_name: '', email: '', phone: '', title: '',
    date_of_birth: '', gender: '',
    // address
    blood_group: '',
    address: { ...blankAddress },
    // employment
    role: '', department: '',
    reporting_manager_id: '', joining_date: '',
    contract_period: '12 months', contract_end_date: '',
    employee_id: '',
    skills: [], tools: [], specialist_roles: [],
    nda_status: 'pending',
    last_company: '', last_designation: '', last_ctc: '',
    notice_period_days: '', total_experience_years: '',
    // ctc
    current_ctc: { ...blankCTC },
    ctc_locked: true, // when true, edits to annual auto-recompute breakdown
    // docs
    documents: [],
    send_welcome_email: true,
  });

  useEffect(() => {
    api.get('/hr/vocabularies').then(r => setVocab(r.data));
    api.get('/hr/managers').then(r => setManagers(r.data.managers || []));
  }, [api]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setAddr = (k, v) => setForm(f => ({ ...f, address: { ...f.address, [k]: v } }));
  const setCTC = (k, v) => setForm(f => ({ ...f, current_ctc: { ...f.current_ctc, [k]: v }, ctc_locked: false }));
  const toggle = (key, v) => set(key, (form[key] || []).includes(v) ? form[key].filter(x => x !== v) : [...(form[key] || []), v]);

  // ---------- CTC auto-prefill ----------
  const recalcCTC = async (annualNum) => {
    if (!annualNum || annualNum <= 0) {
      set('current_ctc', { ...blankCTC });
      return;
    }
    try {
      const r = await api.post('/hr/ctc/calculate', { annual: annualNum });
      setForm(f => ({ ...f, current_ctc: r.data, ctc_locked: true }));
    } catch (e) { /* ignore */ }
  };
  // Reverse calculator — solves the annual CTC + breakdown that produces a
  // given monthly take-home. Used when the founder has promised "₹X take
  // home" to a candidate and needs the components to add up exactly.
  const [reverseNet, setReverseNet] = useState('');
  const [reverseBusy, setReverseBusy] = useState(false);
  const [reverseMsg, setReverseMsg] = useState('');
  const solveFromNet = async () => {
    const target = parseFloat(reverseNet) || 0;
    if (target <= 0) { setReverseMsg('Enter a positive monthly net amount'); return; }
    setReverseBusy(true);
    setReverseMsg('');
    try {
      const r = await api.post('/hr/ctc/from-net', { net_monthly: target });
      const meta = r.data._meta || {};
      const cleaned = { ...r.data };
      delete cleaned._meta;
      setForm(f => ({ ...f, current_ctc: cleaned, ctc_locked: true }));
      const pfMsg = meta.pf_included ? '' : ' · PF skipped (basic < ₹15k)';
      setReverseMsg(`✓ Solved · annual ₹${Number(cleaned.annual).toLocaleString('en-IN')} → net ₹${Number(cleaned.net_monthly).toLocaleString('en-IN')}/mo${pfMsg}`);
    } catch (e) {
      setReverseMsg(e.response?.data?.detail || 'Could not solve — try a different target');
    } finally {
      setReverseBusy(false);
    }
  };
  const onAnnualChange = (raw) => {
    const num = parseFloat(raw) || 0;
    setForm(f => ({ ...f, current_ctc: { ...f.current_ctc, annual: num } }));
  };
  const onAnnualBlur = () => recalcCTC(parseFloat(form.current_ctc.annual) || 0);

  // ---------- Document upload ----------
  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        const r = await api.post(`/hr/employees/document-upload?kind=${encodeURIComponent(docKind)}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setForm(prev => ({ ...prev, documents: [...prev.documents, r.data] }));
      }
    } catch (ex) {
      setErr(ex.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };
  const removeDoc = (id) => set('documents', form.documents.filter(d => d.id !== id));

  // ---------- Submit ----------
  const submit = async () => {
    setBusy(true); setErr('');
    try {
      const payload = {
        ...form,
        full_name: `${form.first_name} ${form.last_name}`.trim(),
        last_ctc: form.last_ctc ? parseFloat(form.last_ctc) : null,
        notice_period_days: form.notice_period_days ? parseInt(form.notice_period_days, 10) : null,
        total_experience_years: form.total_experience_years ? parseFloat(form.total_experience_years) : null,
      };
      delete payload.ctc_locked;
      const r = await api.post('/hr/employees', payload);
      setResult(r.data);
      onCreated?.();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to onboard');
    } finally { setBusy(false); }
  };

  const copyShare = () => {
    navigator.clipboard.writeText(result.share_block);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  // ---------- Appointment letter ----------
  const generateLetter = async () => {
    if (!result?.user_id) return;
    setLetterBusy(true);
    try {
      const r = await api.post(`/hr/employees/${result.user_id}/appointment-letter`);
      setLetterStatus({ kind: 'generated', url: r.data.letter?.url });
    } catch (e) { setErr(e.response?.data?.detail || 'Letter generation failed'); }
    finally { setLetterBusy(false); }
  };
  const dispatchLetter = async () => {
    if (!result?.user_id) return;
    setLetterBusy(true);
    try {
      await api.post(`/hr/employees/${result.user_id}/dispatch-appointment-letter`);
      setLetterStatus(s => ({ ...(s || {}), kind: 'dispatched' }));
    } catch (e) { setErr(e.response?.data?.detail || 'Dispatch failed'); }
    finally { setLetterBusy(false); }
  };

  // ---------- Validations per step ----------
  const stepValid = useMemo(() => {
    if (step === 1) return form.first_name && form.email && form.workforce_type && /\S+@\S+\.\S+/.test(form.email);
    if (step === 2) return true; // address optional but encouraged
    if (step === 3) return form.role && form.department;
    if (step === 4) return true; // CTC optional
    return true;
  }, [step, form]);

  if (!vocab) return null;

  // ============ SUCCESS SCREEN ============
  if (result) {
    return (
      <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center overflow-y-auto py-8" onClick={onClose}>
        <div className="bg-zinc-900 border border-emerald-500/40 rounded-md p-6 w-[40rem] max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="employee-success-modal">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-emerald-400" /></div>
            <div>
              <h3 className="text-base font-heading font-semibold text-white">Person onboarded</h3>
              <p className="text-xs text-zinc-400">{result.full_name} · {result.role} · {result.department}</p>
            </div>
          </div>

          <div className="bg-zinc-950 border border-zinc-800 rounded-md p-3 space-y-1 text-xs">
            <p><span className="text-zinc-400">Email: </span><span className="text-white font-mono">{result.email}</span></p>
            <p><span className="text-zinc-400">Password: </span><span className="text-amber-300 font-mono">{result.temp_password}</span></p>
            <p><span className="text-zinc-400">Login: </span><a href={result.login_url} className="text-cyan-400 underline">{result.login_url}</a></p>
          </div>

          {/* Appointment letter actions */}
          <div className="mt-4 p-3 bg-zinc-950 border border-violet-500/30 rounded-md">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-violet-300" />
              <h4 className="text-xs font-semibold text-violet-200">Appointment Letter</h4>
              {letterStatus?.kind === 'generated' && <span className="ml-auto text-[10px] px-2 py-0.5 rounded bg-amber-500/20 text-amber-300">Generated</span>}
              {letterStatus?.kind === 'dispatched' && <span className="ml-auto text-[10px] px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300">Dispatched</span>}
            </div>
            <p className="text-[11px] text-zinc-400 mb-3">Generate an A4 PDF letter with full CTC breakdown, then dispatch via email to {result.email}.</p>
            <div className="flex flex-wrap gap-2">
              <button onClick={generateLetter} disabled={letterBusy} data-testid="emp-generate-letter-btn"
                className="text-xs px-3 py-1.5 bg-violet-500 hover:bg-violet-400 disabled:opacity-40 rounded-md text-white font-medium flex items-center gap-1.5">
                {letterBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />}
                {letterStatus?.kind ? 'Regenerate' : 'Generate Letter'}
              </button>
              {letterStatus?.url && (
                <a href={`${process.env.REACT_APP_BACKEND_URL || ''}${letterStatus.url}`} target="_blank" rel="noreferrer"
                   data-testid="emp-letter-preview-link"
                   className="text-xs px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-md text-white flex items-center gap-1.5">
                  <FileText className="w-3 h-3" /> Preview PDF
                </a>
              )}
              <button onClick={dispatchLetter} disabled={letterBusy || letterStatus?.kind !== 'generated'} data-testid="emp-dispatch-letter-btn"
                className="text-xs px-3 py-1.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 rounded-md text-white font-medium flex items-center gap-1.5">
                {letterBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                Dispatch via Email
              </button>
            </div>
          </div>

          <pre className="mt-4 p-3 bg-zinc-950 border border-zinc-800 rounded-md text-[10px] text-zinc-300 whitespace-pre-wrap font-mono">{result.share_block}</pre>

          {err && <div className="mt-3 p-2 bg-rose-500/10 border border-rose-500/30 rounded-sm text-[11px] text-rose-300" data-testid="emp-error">{err}</div>}

          <div className="flex justify-end gap-2 mt-4">
            <button onClick={copyShare} data-testid="emp-copy-share" className="text-xs px-3 py-1.5 bg-zinc-800 border border-zinc-700 text-white rounded-md flex items-center gap-1.5">
              <Copy className="w-3 h-3" /> {copied ? 'Copied!' : 'Copy share block'}
            </button>
            <button onClick={onClose} data-testid="emp-success-close" className="text-xs px-3 py-1.5 bg-emerald-500 hover:bg-emerald-400 rounded-md text-white font-medium">Done</button>
          </div>
        </div>
      </div>
    );
  }

  // ============ WIZARD ============
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center overflow-y-auto py-6" onClick={onClose}>
      <div className="bg-zinc-900 border border-zinc-800 rounded-md w-[52rem] max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="employee-onboard-modal">
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-zinc-800">
          <div>
            <div className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-cyan-400" />
              <h3 className="text-base font-heading font-semibold text-white">Onboard New Person</h3>
            </div>
            <p className="text-[11px] text-zinc-400 mt-1">ERP-grade onboarding · creates login · captures HR-grade KYC, CTC and documents.</p>
          </div>
          <button onClick={onClose} data-testid="emp-modal-close"><X className="w-5 h-5 text-zinc-400 hover:text-white" /></button>
        </div>

        {/* Stepper */}
        <div className="flex items-center gap-1 px-5 py-3 border-b border-zinc-800 bg-zinc-950/50">
          {STEPS.map((s, i) => {
            const done = step > s.id;
            const active = step === s.id;
            return (
              <React.Fragment key={s.id}>
                <button onClick={() => s.id < step && setStep(s.id)} data-testid={`emp-step-${s.id}`}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-[11px] transition ${active ? 'bg-cyan-500/15 border border-cyan-500/40 text-cyan-200' : done ? 'text-emerald-300' : 'text-zinc-500'}`}>
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${active ? 'bg-cyan-500 text-zinc-950' : done ? 'bg-emerald-500 text-zinc-950' : 'bg-zinc-800 text-zinc-500'}`}>
                    {done ? <Check className="w-3 h-3" /> : s.id}
                  </span>
                  {s.label}
                </button>
                {i < STEPS.length - 1 && <div className={`flex-1 h-px ${done ? 'bg-emerald-500/40' : 'bg-zinc-800'}`} />}
              </React.Fragment>
            );
          })}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* ────────── STEP 1 — Identity ────────── */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <Label>Workforce Type *</Label>
                <div className="flex gap-1.5 mt-1.5">
                  {(vocab.employment_types || []).map(t => (
                    <button key={t} onClick={() => set('workforce_type', t)} data-testid={`emp-type-${t}`}
                      className={`px-3 py-1.5 rounded-md text-[11px] border capitalize ${form.workforce_type === t ? 'bg-cyan-500/15 border-cyan-500/40 text-cyan-200' : 'bg-zinc-950 border-zinc-800 text-zinc-300 hover:text-white'}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Field label="First Name *" v={form.first_name} on={(v) => set('first_name', v)} testid="emp-firstname" />
                <Field label="Last Name" v={form.last_name} on={(v) => set('last_name', v)} testid="emp-lastname" />
                <Field label="Email *" v={form.email} on={(v) => set('email', v)} testid="emp-email" />
                <Field label="Phone" v={form.phone} on={(v) => set('phone', v)} testid="emp-phone" />
                <Field label="Title / Designation" v={form.title} on={(v) => set('title', v)} testid="emp-title" />
                <Field label="Employee ID" v={form.employee_id} on={(v) => set('employee_id', v)} testid="emp-empid" placeholder="auto-generated if blank" />
                <Field label="Date of Birth" type="date" v={form.date_of_birth} on={(v) => set('date_of_birth', v)} testid="emp-dob" />
                <Select label="Gender" v={form.gender} on={(v) => set('gender', v)} testid="emp-gender"
                  options={['', 'male', 'female', 'non_binary', 'prefer_not_to_say']} />
              </div>
            </div>
          )}

          {/* ────────── STEP 2 — Address ────────── */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Field label="Street / House" v={form.address.street} on={(v) => setAddr('street', v)} testid="emp-addr-street" /></div>
                <Field label="City" v={form.address.city} on={(v) => setAddr('city', v)} testid="emp-addr-city" />
                <Field label="State" v={form.address.state} on={(v) => setAddr('state', v)} testid="emp-addr-state" />
                <Field label="Pincode / ZIP" v={form.address.pincode} on={(v) => setAddr('pincode', v)} testid="emp-addr-pincode" />
                <Field label="Country" v={form.address.country} on={(v) => setAddr('country', v)} testid="emp-addr-country" />
                <Field label="Landmark" v={form.address.landmark} on={(v) => setAddr('landmark', v)} testid="emp-addr-landmark" />
                <Select label="Blood Group" v={form.blood_group} on={(v) => set('blood_group', v)} testid="emp-blood"
                  options={['', 'A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-']} />
              </div>
            </div>
          )}

          {/* ────────── STEP 3 — Employment ────────── */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Select label="Role *" v={form.role} on={(v) => set('role', v)} testid="emp-role"
                  options={(vocab.roles || []).map(r => ({ value: r.key, label: r.label + (r.source === 'custom' ? ' (custom)' : '') }))} />
                <Select label="Department *" v={form.department} on={(v) => set('department', v)} testid="emp-department"
                  options={vocab.departments || []} />
                <Select label="Reporting Manager" v={form.reporting_manager_id} on={(v) => set('reporting_manager_id', v)} testid="emp-manager"
                  options={[{ value: '', label: '— None —' }, ...managers.map(m => ({ value: m.id, label: `${m.full_name} · ${m.role}` }))]} />
                <Field label="Joining Date" type="date" v={form.joining_date} on={(v) => set('joining_date', v)} testid="emp-joining" />
                <Field label="Contract Period" v={form.contract_period} on={(v) => set('contract_period', v)} testid="emp-contract" />
                <Field label="Contract End Date" type="date" v={form.contract_end_date} on={(v) => set('contract_end_date', v)} testid="emp-contract-end" />
                <Select label="NDA Status" v={form.nda_status} on={(v) => set('nda_status', v)} testid="emp-nda"
                  options={['pending', 'sent', 'signed', 'not_required']} />
              </div>

              <div className="pt-3 border-t border-zinc-800">
                <Label>Previous Employment</Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <Field label="Last Company" v={form.last_company} on={(v) => set('last_company', v)} testid="emp-lastco" />
                  <Field label="Last Designation" v={form.last_designation} on={(v) => set('last_designation', v)} testid="emp-lastdesig" />
                  <Field label="Last CTC (₹/year)" type="number" v={form.last_ctc} on={(v) => set('last_ctc', v)} testid="emp-lastctc" />
                  <Field label="Notice Period (days)" type="number" v={form.notice_period_days} on={(v) => set('notice_period_days', v)} testid="emp-notice" />
                  <Field label="Total Experience (years)" type="number" v={form.total_experience_years} on={(v) => set('total_experience_years', v)} testid="emp-experience" />
                </div>
              </div>

              <div className="pt-3 border-t border-zinc-800">
                <Label>Skills</Label>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(vocab.skills || []).map(s => (
                    <button key={s} onClick={() => toggle('skills', s)} data-testid={`emp-skill-${s}`}
                      className={`text-[11px] px-2 py-1 rounded ${form.skills.includes(s) ? 'bg-cyan-500/20 text-cyan-200' : 'bg-zinc-800 text-zinc-300 hover:text-white'}`}>
                      {s.replace(/_/g, ' ')}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Tools</Label>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(vocab.tools || []).map(t => (
                    <button key={t} onClick={() => toggle('tools', t)} data-testid={`emp-tool-${t}`}
                      className={`text-[11px] px-2 py-1 rounded ${form.tools.includes(t) ? 'bg-violet-500/20 text-violet-200' : 'bg-zinc-800 text-zinc-300 hover:text-white'}`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ────────── STEP 4 — Compensation ────────── */}
          {step === 4 && (
            <div className="space-y-4">
              {/* Reverse calculator — solve forward CTC from a target take-home. */}
              <div className="bg-emerald-500/5 border border-emerald-500/30 rounded-md p-4" data-testid="emp-ctc-reverse-block">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] uppercase tracking-wider text-emerald-300 font-semibold">Calculate from Net Take-home</span>
                  <span className="text-[9px] text-zinc-400">— enter the monthly figure you've promised the candidate; we'll solve the components.</span>
                </div>
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <Label>Net / Month (₹)</Label>
                    <input type="number" value={reverseNet}
                      onChange={(e) => setReverseNet(e.target.value)}
                      data-testid="emp-ctc-reverse-net"
                      className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-emerald-500/40 rounded-md text-base text-white font-mono focus:outline-none focus:border-emerald-400"
                      placeholder="e.g. 10000" />
                  </div>
                  <button onClick={solveFromNet} disabled={reverseBusy} data-testid="emp-ctc-reverse-solve"
                    className="text-[11px] px-3 py-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 rounded-md text-white font-medium">
                    {reverseBusy ? 'Solving…' : 'Solve & Fill'}
                  </button>
                </div>
                {reverseMsg && <p className="text-[10px] mt-2 text-emerald-200" data-testid="emp-ctc-reverse-msg">{reverseMsg}</p>}
              </div>
              <div className="bg-violet-500/5 border border-violet-500/30 rounded-md p-4">
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <Label>Annual CTC (₹) *</Label>
                    <input type="number" value={form.current_ctc.annual || ''}
                      onChange={(e) => onAnnualChange(e.target.value)}
                      onBlur={onAnnualBlur}
                      data-testid="emp-ctc-annual"
                      className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-violet-500/40 rounded-md text-base text-white font-mono focus:outline-none focus:border-violet-400" placeholder="e.g. 800000" />
                  </div>
                  <button onClick={onAnnualBlur} data-testid="emp-ctc-recalc"
                    className="text-[11px] px-3 py-2 bg-violet-500 hover:bg-violet-400 rounded-md text-white font-medium">
                    Recalculate
                  </button>
                </div>
                <p className="text-[10px] text-zinc-400 mt-2">Indian-standard prefill: Basic 40% · HRA 20% · Special 30% · PF 12% (only if basic ≥ ₹15k/mo) · TDS new regime. Every row below is editable.</p>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <CTCField label="Basic" k="basic" form={form} onChange={setCTC} />
                <CTCField label="HRA" k="hra" form={form} onChange={setCTC} />
                <CTCField label="Special Allowance" k="special_allowance" form={form} onChange={setCTC} />
                <CTCField label="Gross (Annual)" k="gross_annual" form={form} onChange={setCTC} />
                <CTCField label="Gross (Monthly)" k="gross_monthly" form={form} onChange={setCTC} />
                <CTCField label="PF (Employee)" k="pf_employee" form={form} onChange={setCTC} />
                <CTCField label="PF (Employer)" k="pf_employer" form={form} onChange={setCTC} />
                <CTCField label="Professional Tax" k="prof_tax" form={form} onChange={setCTC} />
                <CTCField label="TDS (Annual)" k="tds_annual" form={form} onChange={setCTC} />
                <CTCField label="TDS (Monthly)" k="tds_monthly" form={form} onChange={setCTC} />
                <CTCField label="Net Take-home (Annual)" k="net_annual" form={form} onChange={setCTC} highlight />
                <CTCField label="Net Take-home (Monthly)" k="net_monthly" form={form} onChange={setCTC} highlight />
              </div>
            </div>
          )}

          {/* ────────── STEP 5 — Documents & Review ────────── */}
          {step === 5 && (
            <div className="space-y-4">
              <div className="bg-zinc-950 border border-zinc-800 rounded-md p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Upload className="w-4 h-4 text-cyan-300" />
                  <h4 className="text-xs font-semibold text-white">KYC & Onboarding Documents</h4>
                </div>
                <div className="flex items-end gap-2 mb-3">
                  <div className="flex-1">
                    <Label>Document Kind</Label>
                    <select value={docKind} onChange={(e) => setDocKind(e.target.value)} data-testid="emp-doc-kind"
                      className="w-full mt-1 px-2 py-1.5 bg-zinc-900 border border-zinc-700 rounded-md text-xs text-white">
                      {DOC_KINDS.map(k => <option key={k.v} value={k.v}>{k.l}</option>)}
                    </select>
                  </div>
                  <label data-testid="emp-doc-upload" className="text-xs px-3 py-2 bg-cyan-500 hover:bg-cyan-400 rounded-md text-white font-medium cursor-pointer flex items-center gap-1.5">
                    {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Upload
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xlsx,.csv" multiple onChange={onUpload} className="hidden" />
                  </label>
                </div>
                {form.documents.length === 0 ? (
                  <p className="text-[11px] text-zinc-400">No documents uploaded yet. Accepted: PDF, JPG, PNG, DOC, DOCX, XLSX, CSV (≤25MB each).</p>
                ) : (
                  <div className="space-y-1" data-testid="emp-docs-list">
                    {form.documents.map(d => (
                      <div key={d.id} className="flex items-center gap-2 text-xs bg-zinc-900 border border-zinc-800 rounded px-2 py-1.5">
                        <FileText className="w-3.5 h-3.5 text-zinc-300" />
                        <span className="text-white truncate flex-1">{d.filename}</span>
                        <span className="text-[10px] text-zinc-400">{d.kind}</span>
                        <span className="text-[10px] text-zinc-400">{Math.round((d.size || 0) / 1024)} KB</span>
                        <button onClick={() => removeDoc(d.id)} data-testid={`emp-doc-remove-${d.id}`}><Trash2 className="w-3 h-3 text-rose-400 hover:text-rose-300" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-zinc-950 border border-zinc-800 rounded-md p-4">
                <h4 className="text-xs font-semibold text-white mb-3">Summary</h4>
                <SummaryRow k="Name" v={`${form.first_name} ${form.last_name}`.trim() || '—'} />
                <SummaryRow k="Email" v={form.email || '—'} />
                <SummaryRow k="Type" v={form.workforce_type} />
                <SummaryRow k="Role · Dept" v={`${form.role || '—'} · ${form.department || '—'}`} />
                <SummaryRow k="Joining" v={form.joining_date || '—'} />
                <SummaryRow k="Annual CTC" v={form.current_ctc.annual ? `₹${Number(form.current_ctc.annual).toLocaleString('en-IN')}` : '—'} />
                <SummaryRow k="Net (Monthly)" v={form.current_ctc.net_monthly ? `₹${Number(form.current_ctc.net_monthly).toLocaleString('en-IN')}` : '—'} />
                <SummaryRow k="Documents" v={`${form.documents.length} attached`} />
              </div>

              <label className="flex items-center gap-2 text-xs text-zinc-200">
                <input type="checkbox" checked={form.send_welcome_email} onChange={e => set('send_welcome_email', e.target.checked)} data-testid="emp-send-email" />
                <Mail className="w-3.5 h-3.5 text-cyan-300" />
                Send welcome email with login credentials
              </label>
            </div>
          )}

          {err && (
            <div className="mt-4 p-2.5 bg-rose-500/10 border border-rose-500/30 rounded-md flex items-start gap-2 text-xs text-rose-300" data-testid="emp-error">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> {err}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center gap-2 p-4 border-t border-zinc-800">
          <span className="text-[11px] text-zinc-400">Step {step} of {STEPS.length}</span>
          <div className="flex gap-2">
            {step > 1 && (
              <button onClick={() => setStep(step - 1)} data-testid="emp-prev-btn"
                className="text-xs px-3 py-1.5 bg-zinc-800 border border-zinc-700 text-white rounded-md flex items-center gap-1.5 hover:bg-zinc-700">
                <ChevronLeft className="w-3 h-3" /> Back
              </button>
            )}
            {step < STEPS.length ? (
              <button onClick={() => stepValid && setStep(step + 1)} disabled={!stepValid} data-testid="emp-next-btn"
                className="text-xs px-4 py-1.5 bg-cyan-500 disabled:opacity-30 hover:bg-cyan-400 rounded-md text-white font-medium flex items-center gap-1.5">
                Next <ChevronRight className="w-3 h-3" />
              </button>
            ) : (
              <button onClick={submit} disabled={busy || !form.first_name || !form.email || !form.role || !form.department}
                data-testid="emp-submit-btn"
                className="text-xs px-4 py-1.5 bg-emerald-500 disabled:opacity-30 hover:bg-emerald-400 rounded-md text-white font-medium flex items-center gap-1.5">
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3 h-3" />}
                Create Person
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ────────── shared atoms ──────────
const Label = ({ children }) => <label className="text-[10px] text-zinc-300 uppercase tracking-wider font-medium">{children}</label>;

const Field = ({ label, v, on, type = 'text', testid, placeholder }) => (
  <div>
    <Label>{label}</Label>
    <input value={v ?? ''} onChange={e => on(e.target.value)} type={type} data-testid={testid} placeholder={placeholder}
      className="w-full mt-1 px-2.5 py-1.5 bg-zinc-950 border border-zinc-800 rounded-md text-xs text-white focus:outline-none focus:border-cyan-500/50 placeholder:text-zinc-600" />
  </div>
);

const Select = ({ label, v, on, options, testid }) => (
  <div>
    <Label>{label}</Label>
    <select value={v ?? ''} onChange={e => on(e.target.value)} data-testid={testid}
      className="w-full mt-1 px-2.5 py-1.5 bg-zinc-950 border border-zinc-800 rounded-md text-xs text-white focus:outline-none focus:border-cyan-500/50">
      {options.map(o => {
        const val = typeof o === 'string' ? o : o.value;
        const lbl = typeof o === 'string' ? (o ? o.replace(/_/g, ' ') : '—') : o.label;
        return <option key={val} value={val}>{lbl}</option>;
      })}
    </select>
  </div>
);

const CTCField = ({ label, k, form, onChange, highlight }) => (
  <div>
    <Label>{label}</Label>
    <input type="number" value={form.current_ctc[k] || ''}
      onChange={e => onChange(k, parseFloat(e.target.value) || 0)}
      data-testid={`emp-ctc-${k}`}
      className={`w-full mt-1 px-2 py-1.5 rounded-md text-xs font-mono focus:outline-none ${highlight ? 'bg-emerald-500/10 border border-emerald-500/40 text-emerald-200' : 'bg-zinc-950 border border-zinc-800 text-white focus:border-violet-500/50'}`} />
  </div>
);

const SummaryRow = ({ k, v }) => (
  <div className="flex items-center justify-between py-1 text-xs border-b border-zinc-800/50 last:border-0">
    <span className="text-zinc-400">{k}</span>
    <span className="text-white">{v}</span>
  </div>
);
